import { Link } from 'react-router-dom';
import { Skeleton } from '@ah/design';
import { MapPin, Video, CalendarDays } from 'lucide-react';
import type { Gathering } from '../shared/index.ts';

function rsvpBadge(gathering: Gathering): { label: string; className: string } | null {
  const status = gathering.currentUserParticipant?.status ?? null;
  if (status === 'attending' || status === 'confirmed' || status === 'going') {
    return { label: 'Attending', className: 'bg-emerald-500/10 text-emerald-700' };
  }
  if (status === 'declined' || status === 'not_going') {
    return { label: 'Declined', className: 'bg-muted text-muted-foreground' };
  }
  if (status === 'invited' || status === 'pending') {
    return { label: 'Invited', className: 'bg-amber-500/10 text-amber-700' };
  }
  return null;
}

export function GatheringRow({ gathering }: { gathering: Gathering }) {
  const start = new Date(gathering.startsAt);
  const day = start.toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short' });
  const time = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const isOnline = gathering.kind === 'roundtable';
  const badge = rsvpBadge(gathering);

  return (
    <Link
      to={`/gatherings/${gathering.id}`}
      className="flex items-stretch bg-card rounded-xl border border-border overflow-hidden active:bg-accent"
    >
      <div className="w-[84px] shrink-0 bg-muted/60 border-r border-border flex flex-col items-center justify-center">
        {gathering.coverUrl ? (
          <img
            src={gathering.coverUrl}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {start.toLocaleDateString('en-US', { month: 'short' })}
            </span>
            <span className="text-2xl font-bold leading-none mt-1 text-foreground">
              {start.getDate()}
            </span>
          </>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-0.5 p-4">
        <div className="flex items-start gap-2">
          <h2 className="text-sm font-semibold leading-snug line-clamp-2 flex-1">{gathering.title}</h2>
          {badge && (
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${badge.className}`}>
              {badge.label}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
          <CalendarDays size={11} /> {day} · {time}
        </p>
        {isOnline ? (
          <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <Video size={11} /> Online
          </p>
        ) : gathering.location ? (
          <p className="text-xs text-muted-foreground inline-flex items-center gap-1 min-w-0">
            <MapPin size={11} className="shrink-0" />
            <span className="truncate">{gathering.location}</span>
          </p>
        ) : null}
      </div>
    </Link>
  );
}

export function GatheringsListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex items-stretch bg-card rounded-xl border border-border overflow-hidden">
          <Skeleton className="w-[84px] shrink-0 rounded-none" />
          <div className="flex-1 space-y-2 p-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ErrorRetry({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <div className="text-center py-16">
      <p className="text-sm text-destructive mb-3">{label}</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-sm px-3 py-1.5 rounded-full border border-border bg-card hover:bg-accent"
      >
        Retry
      </button>
    </div>
  );
}
